import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";
import type { Container } from "@egalregal/shared";
import { ContainerMetaChips } from "../../shared/components/ContainerMetaChips";
import { PrintSheetDialog } from "./PrintSheetDialog";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";

/** Rows per page for the print selection list; keeps long catalogs reachable. */
export const PRINT_PAGE_SIZE = 20;

/** Quick filter for the print selection list (domain: container kind). */
export type PrintKindFilter = "all" | "box" | "nonbox";

/**
 * Whether a container kind counts as a box (case-insensitive, trimmed).
 *
 * @param kind the container's kind label.
 * @returns true when the kind is "box", false for every other kind.
 */
export function isBoxKind(kind: string): boolean {
  return kind.trim().toLowerCase() === "box";
}

/**
 * Filter containers for the print selection list.
 *
 * @param containers the full visible container list.
 * @param filter the active quick filter.
 * @returns the containers matching the filter, in input order.
 */
export function filterPrintContainers(containers: Container[], filter: PrintKindFilter): Container[] {
  if (filter === "box") return containers.filter((c) => isBoxKind(c.kind));
  if (filter === "nonbox") return containers.filter((c) => !isBoxKind(c.kind));
  return containers;
}

/**
 * Resolve selected ids to containers in the order the user picked them. The
 * dialog previews the first pick, so list order is not good enough.
 *
 * @param selected selected container ids, in click order.
 * @param containers the current container list.
 * @returns the selected containers in selection order; disappeared ids are dropped.
 */
export function pickInSelectionOrder(
  selected: ReadonlySet<string>,
  containers: readonly Container[],
): Container[] {
  const byId = new Map(containers.map((container) => [container.id, container]));
  return [...selected].flatMap((id) => {
    const container = byId.get(id);
    return container ? [container] : [];
  });
}

/**
 * Multi-select → QR label sheet (ticket 09). Selection lives here, the grid
 * preview + size picker + browser print live in PrintSheetDialog. The list is
 * paginated (PRINT_PAGE_SIZE) with All/Boxes/Non-boxes quick filters so every
 * label stays reachable; selection persists across pages and filters.
 */
export function PrintView() {
  const t = useT();
  const containers = useAppStore((s) => s.containers);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [blank, setBlank] = useState(false);
  const [filter, setFilter] = useState<PrintKindFilter>("all");
  const [page, setPage] = useState(1);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const changeFilter = (next: PrintKindFilter) => {
    setFilter(next);
    setPage(1);
  };

  const filtered = useMemo(() => filterPrintContainers(containers, filter), [containers, filter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PRINT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  // Clamp the stored page when the list shrinks underneath it (e.g. catalog
  // switch or deletions); filter changes already reset to page 1.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  const start = filtered.length === 0 ? 0 : (safePage - 1) * PRINT_PAGE_SIZE + 1;
  const end = Math.min(safePage * PRINT_PAGE_SIZE, filtered.length);
  const visible = filtered.slice((safePage - 1) * PRINT_PAGE_SIZE, safePage * PRINT_PAGE_SIZE);

  const picked = pickInSelectionOrder(selected, containers);

  return (
    <>
      <ButtonGroup aria-label={t("printKindFilter")} sx={{ mb: 1 }}>
        <Button variant={filter === "all" ? "contained" : "outlined"} onClick={() => changeFilter("all")}>
          {t("printFilterAll")}
        </Button>
        <Button variant={filter === "box" ? "contained" : "outlined"} onClick={() => changeFilter("box")}>
          {t("printFilterBoxes")}
        </Button>
        <Button variant={filter === "nonbox" ? "contained" : "outlined"} onClick={() => changeFilter("nonbox")}>
          {t("printFilterNonBoxes")}
        </Button>
      </ButtonGroup>
      {containers.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          {t("noContainers")}
        </Typography>
      ) : filtered.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          {t("noMatches")}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("printShowing", {
              start: String(start),
              end: String(end),
              total: String(filtered.length),
            })}
          </Typography>
          <List>
            {visible.map((c) => (
              <ListItem key={c.id} disablePadding secondaryAction={<ContainerMetaChips owner={c.owner} kind={c.kind} sx={{ ml: 1 }} />}>
                <ListItemButton onClick={() => toggle(c.id)}>
                  <Checkbox edge="start" checked={selected.has(c.id)} tabIndex={-1} disableRipple aria-label={`Select ${c.name}`} />
                  <ListItemText primary={c.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Pagination count={pageCount} page={safePage} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}
      <Button
        variant="contained"
        disabled={picked.length === 0}
        onClick={() => {
          setBlank(false);
          setOpen(true);
        }}
        sx={{ mt: 2, mr: 1 }}
      >
        {t("printLabels", { count: String(picked.length) })}
      </Button>
      <Button
        variant="outlined"
        onClick={() => {
          setBlank(true);
          setOpen(true);
        }}
        sx={{ mt: 2 }}
      >
        {t("printBlank")}
      </Button>

      <PrintSheetDialog open={open} containers={picked} blank={blank} onClose={() => setOpen(false)} />
    </>
  );
}